import React from 'react';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ContentSection } from './components/ContentSection';
import { CTAButton } from './components/CTAButton';
import { FAQ } from './components/FAQ';
import { Footer } from './components/Footer';
import { WeekAccordion } from './components/WeekAccordion';
import { PricingCard } from './components/PricingCard';
import { GuaranteeBadge } from './components/GuaranteeBadge';
import { TestimonialsSection } from './components/TestimonialsSection';
import { LimitedTimeOffer } from './components/LimitedTimeOffer';
import { TrustpilotReviews } from './components/TrustpilotReviews';
import { CheckIcon, LockIcon } from 'lucide-react';
export function App() {
  return <div className="w-full min-h-full font-sans bg-white" data-id="element-0">
      <Header data-id="element-1" />
      <main className="w-full max-w-4xl mx-auto px-4 sm:px-6" data-id="element-2">
        <Hero data-id="element-3" />
        <ContentSection title="I've worked with hundreds of clients who felt the exact same way." content={<>
              <p className="text-gray-700 mb-4" data-id="element-5">
                People who were smart, sensitive, strong — and still found
                themselves spiraling over what seemed like nothing.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-6">
                It didn't matter how many tests came back normal. Or how many
                times they were told "you're fine." Their body didn't feel fine.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-7">
                A skipped heartbeat could send them into panic. A dizzy spell
                could ruin their day. Even just thinking about their symptoms
                could trigger a full-blown wave of fear.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-8">
                Because for them… and maybe for you too… the fear doesn't start
                in the mind. It starts in the body.
              </p>
            </>} data-id="element-4" />
        <ContentSection title="A strange sensation. A quick flash of discomfort. And suddenly their nervous system floods with alarm." content={<>
              <p className="text-gray-700 mb-4" data-id="element-10">
                Their heart races. Their breathing changes. Their thoughts spin
                out of control.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-11">And the hardest part?</p>
              <p className="text-gray-700 mb-4" data-id="element-12">
                Sometimes the panic doesn't even need a symptom anymore.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-13">
                Just remembering the last time it happened is enough to set the
                spiral in motion.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-14">
                This pattern — where fear starts in the body and races into the
                mind — is something I see all the time.
              </p>
            </>} data-id="element-9" />
        <ContentSection title="It's called interoceptive anxiety." content={<>
              <p className="text-gray-700 mb-4" data-id="element-16">
                And if no one's ever explained it to you, you might think:
              </p>
              <p className="text-gray-700 mb-4" data-id="element-17">
                "Why can't I just stop worrying?" "Why does my body overreact
                like this?" "Why do I feel so scared — even when nothing's
                wrong?"
              </p>
              <p className="text-gray-700 mb-4" data-id="element-18">
                But what I've seen, again and again, is that these reactions are
                not overreactions. They're messages from a nervous system that's
                been stuck in survival mode for far too long.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-19">
                When your body can't tell the difference between real danger and
                imagined danger — even small sensations can feel terrifying.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-20">
                Your nervous system goes into high alert. And your body responds
                like your life is on the line… Even if you're just sitting on
                your couch.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-21">
                Hi, my name is Dr. Kim DiRe'.
              </p>
            </>} data-id="element-15" />
        <div className="w-full my-8" data-id="element-22">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sTWybtYX2ggUQm5RJ6eYKf/320x400.jpg" alt="Dr. Kim DiRe - Nervous System Expert" className="w-full max-w-md mx-auto h-auto object-cover rounded-lg shadow-md" data-id="element-23" />
        </div>
        <ContentSection title="I've spent over 25 years helping people heal from anxiety, trauma, grief, and emotional overwhelm." content={<>
              <p className="text-gray-700 mb-4" data-id="element-25">
                I hold a doctorate in Behavioral Health. I've trained in somatic
                therapy, hypnotherapy, and nervous system regulation. I've
                worked with thousands of people who tried everything — therapy,
                journaling, supplements, mindfulness — and still couldn't feel
                calm.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-26">
                They were told it was all in their head. They were told to "just
                relax." They were told nothing was wrong.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-27">
                But something was wrong. They just didn't have the language or
                the tools to name it yet.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-28">
                And over time, I began to see a pattern:
              </p>
            </>} data-id="element-24" />
        <ContentSection title="You're not broken. You're not too sensitive. You're not overreacting. It's not just in your head." containerClass="bg-gradient-to-r from-teal-50 to-blue-50 shadow-sm" content={<>
              <p className="text-gray-700 mb-4" data-id="element-30">
                Your body has been stuck in a constant state of threat.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-31">
                Because the part of your nervous system that tells you "you're
                safe" has gone offline...
              </p>
              <p className="text-gray-700 mb-4 font-bold" data-id="element-32">Your Vagus nerve.</p>
              <p className="text-gray-700 mb-4" data-id="element-33">
                And most people have never even heard of it.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-34">
                But once you understand what it does, everything starts to make
                sense.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-35">
                Your vagus nerve is a long, wandering nerve that runs from your
                brainstem, down through your neck, chest, and into your gut.
              </p>
              <div className="w-full my-6" data-id="element-36">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/5sRPWyMmnEvF7F575Zr7B9/ChatGPT_Image_Jun_22%2C_2025%2C_05_39_52_PM.png" alt="Vagus nerve states showing regulated, hyperaroused, and shutdown states" className="w-full max-w-2xl mx-auto h-auto object-contain rounded-lg shadow-md" data-id="element-37" />
              </div>
              <p className="text-gray-700 mb-4" data-id="element-38">
                It touches your heart. Your lungs. Your digestive system.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-39">
                It's like the control switch for your inner state of calm.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-40">
                When your vagus nerve is working properly, it tells your body:
              </p>
              <p className="text-gray-700 mb-4" data-id="element-41">
                "You're safe. You can breathe deeply. You can rest. You can
                connect."
              </p>
              <p className="text-gray-700 mb-4" data-id="element-42">
                But when it's weak, stuck, or dysregulated?
              </p>
              <p className="text-gray-700 mb-4" data-id="element-43">
                That message doesn't get through.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-44">
                Instead, your body stays tense, wired, or numb—even when your
                life looks "fine" on the outside.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-45">
                That's why your chest feels tight but you can't explain it...
              </p>
              <p className="text-gray-700 mb-4" data-id="element-46">
                Why random waves of anxiety and dread take over you without
                notice...
              </p>
              <p className="text-gray-700 mb-4" data-id="element-47">
                Why you're constantly bracing for something bad to happen...
                even if nothing's wrong
              </p>
              <p className="text-gray-700 mb-4" data-id="element-48">
                It's how your nervous system is wired for survival.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-49">
                And when your vagus nerve gets stuck in "threat mode," your
                whole system follows.
              </p>
            </>} data-id="element-29" />
        <div className="w-full my-8" data-id="element-50">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/u7EdUVvhjPzLXyDwch3qS2/d7c88c2-5d02-823-fce2-70ff110181d_41407_2024_2062_Fig1_HTML_%281%29.jpg" alt="Vagus nerve visualization showing how it connects to organs" className="w-full max-w-2xl mx-auto h-auto object-contain rounded-lg shadow-md" data-id="element-51" />
        </div>
        <ContentSection title="Most traditional methods like <span class='text-red-600'>talk therapy</span> and <span class='text-red-600'>medication</span> don't address this." content={<>
              <p className="text-gray-700 mb-4" data-id="element-53">
                They focus on thoughts, mindset, talk therapy.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-54">
                But when the body is the one reacting, you can't out-think it.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-55">
                That's why I began integeating somatic tools into my practice
                before they took over Instagram…
              </p>
              <p className="text-gray-700 mb-4" data-id="element-56">
                Not just mental exercises — but physical, body-based practices
                that target the vagus nerve directly.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-57">
                And when I started using those tools with my clients?
              </p>
              <p className="text-gray-700 mb-4" data-id="element-58">
                That's when everything began to change.
              </p>
            </>} data-id="element-52" />
        <div className="w-full my-8" data-id="element-59">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tC5yKYsqrpgEG1Wc6JgXdJ/17cef8e-2885-4324-66a2-30e65137f44_fibromyalgiaNervePainBrain-1067733668-770x553_jpg.webp" alt="Brain with neural pathways" className="w-full max-w-2xl mx-auto h-auto object-contain rounded-lg shadow-md" data-id="element-60" />
        </div>
        <ContentSection title="That's why I developed a method that helps people retrain their vagus nerve based on nervous system science and gentle somatic practices." content={<>
              <p className="text-gray-700 mb-4" data-id="element-62">
                The practices are short. Just a few minutes a day. But they
                speak the language of the body.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-63">
                I showed my clients how to use breath, movement, grounding, and
                sound to send signals of safety to their nervous system — even
                in the middle of a spiral.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-64">
                And over time, they began to notice something amazing:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700" data-id="element-65">
                <li className="mb-2" data-id="element-66">The spirals stopped coming so often</li>
                <li className="mb-2" data-id="element-67">Their symptoms didn't feel as scary</li>
                <li className="mb-2" data-id="element-68">Their thoughts slowed down</li>
                <li className="mb-2" data-id="element-69">
                  And most of all — their body felt calm again
                </li>
              </ul>
            </>} data-id="element-61" />
        <ContentSection title="Clients who used to call their doctor after every sensation stopped making those calls." content={<>
              <p className="text-gray-700 mb-4" data-id="element-71">
                Clients who couldn't sleep because of racing thoughts started
                sleeping through the night.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-72">
                Clients who felt afraid all the time started to trust their
                bodies again.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-73">
                The key wasn't in doing more.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-74">
                It was in finally working with the nervous system — not against
                it.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-75">
                I took everything I learned — from my academic research, my
                clinical experience, and real-life results — and turned it into
                a gentle, step-by-step system.
              </p>
            </>} data-id="element-70" />
        <div className="w-full my-12 p-8 bg-gradient-to-r from-teal-100 to-blue-100 rounded-xl shadow-lg border-2 border-teal-400 transform -rotate-1" data-id="element-76">
          <ContentSection title="It's called Neurotoned, a powerful 30-day nervous system reset that combines science-backed somatic therapy with daily vagus nerve training." containerClass="" content={<>
                <p className="text-gray-800 mb-4 text-center" data-id="element-78">
                  It's not a course.
                </p>
                <p className="text-gray-800 mb-4 text-center" data-id="element-79">
                  It's not therapy.
                </p>
                <p className="text-gray-800 mb-4 text-center" data-id="element-80">
                  And it's not another mindset program that tells you to "just
                  breathe."
                </p>
                <p className="text-gray-800 mb-6 text-center" data-id="element-81">
                  Neurotoned is a step-by-step, body-first system designed to
                  gently rewire the fear and stress patterns that keep you
                  stuck.
                </p>
                <p className="text-gray-800 mb-4 font-semibold text-center" data-id="element-82">
                  Inside, you'll get:
                </p>
                <div className="space-y-6 mt-6" data-id="element-83">
                  <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-84">
                    <p className="font-bold text-teal-800 mb-2" data-id="element-85">
                      ✅ Short Daily Somatic Practices
                    </p>
                    <p className="text-gray-700" data-id="element-86">
                      Guided by trauma-informed experts to restore vagus nerve
                      tone and bring your body out of survival mode.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-87">
                    <p className="font-bold text-teal-800 mb-2" data-id="element-88">
                      ✅ Regulation Tools That Actually Work
                    </p>
                    <p className="text-gray-700" data-id="element-89">
                      Techniques I've used with thousands of clients to calm
                      panic, stop spirals, and ground your body in real-time.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-90">
                    <p className="font-bold text-teal-800 mb-2" data-id="element-91">
                      ✅ Nervous System Education (That Makes Sense)
                    </p>
                    <p className="text-gray-700" data-id="element-92">
                      You'll finally understand why your body reacts the way it
                      does—and how to shift it, gently and safely.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-93">
                    <p className="font-bold text-teal-800 mb-2" data-id="element-94">
                      ✅ A Roadmap Back to Safety
                    </p>
                    <p className="text-gray-700" data-id="element-95">
                      Week by week, you'll learn how to re-establish a felt
                      sense of calm, connection, and wholeness—in your own body.
                    </p>
                  </div>
                </div>
                <p className="text-gray-800 mt-6 mb-2 text-center" data-id="element-96">
                  This isn't about talking through your trauma.
                </p>
                <p className="text-gray-800 mb-6 text-center" data-id="element-97">
                  It's about healing the residue of it that still lives in your
                  body.
                </p>
                <p className="text-gray-800 mb-2 text-center" data-id="element-98">
                  And once you learn how to turn your vagus nerve back on?
                </p>
                <p className="text-gray-800 mb-4 text-center font-bold text-xl" data-id="element-99">
                  Everything changes.
                </p>
                <LimitedTimeOffer data-id="element-100" />
                <div className="flex justify-center" data-id="element-101">
                  <CTAButton text="Yes — I'm Ready to Reset My Nervous System for Just $29" data-id="element-102" />
                </div>
              </>} data-id="element-77" />
        </div>
        <TrustpilotReviews data-id="element-103" />
        <ContentSection title="Healing your nervous system doesn't happen overnight." content={<>
              <p className="text-gray-700 mb-4" data-id="element-105">
                It happens through small, safe steps — done consistently, with
                care.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-106">
                That's why I created the Neurotoned Method as a 30-day journey.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-107">
                A daily invitation to reconnect with your body, regulate your
                vagus nerve, and gently shift out of survival mode.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-108">
                This isn't a "fix." You're not broken.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-109">
                It's a nervous system reset — rooted in science, guided by
                empathy, and built around real transformation.
              </p>
              <p className="text-gray-700 mb-4" data-id="element-110">Here's how it works:</p>
            </>} data-id="element-104" />
        <div className="w-full my-8" data-id="element-111">
          <WeekAccordion week="W1" title="Safety & Grounding" defaultOpen={true} data-id="element-112">
            <p className="text-gray-700 mb-4 font-medium" data-id="element-113">
              In this first week, we focus on helping your body feel safe enough
              to begin.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-114">
              Most people who come to this program have been stuck in survival
              mode for years — feeling braced, on edge, and disconnected. That's
              why we don't start by going deep. We start by going slow.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-115">
              You'll learn simple tools to settle your system, reconnect with
              your breath, and begin rebuilding a foundation of trust between
              you and your body.
            </p>
            <p className="font-semibold text-teal-800 mb-2" data-id="element-116">
              Daily themes include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 font-medium" data-id="element-117">
              <li className="mb-2" data-id="element-118">
                Grounding your body through stillness and breath
              </li>
              <li className="mb-2" data-id="element-119">
                Reconnecting with internal sensations in a non-overwhelming way
              </li>
              <li className="mb-2" data-id="element-120">
                Gentle movements to create a sense of presence and support
              </li>
              <li className="mb-2" data-id="element-121">
                Identifying your personal "anchors" — the signals of safety that
                already exist within you
              </li>
            </ul>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-122">
              You'll be supported by Demi-Gea Hart, a holistic psychologist who
              blends integrative therapy with movement, breathwork, and inner
              listening. Her practices are empowering and intuitive, perfect for
              building trust in your own body's natural rhythms.
            </p>
            <p className="text-gray-700 mb-4 italic font-medium" data-id="element-123">
              This is often the week where people say: "I finally cried — and it
              felt good." "I didn't realize how much I was holding until I let
              it move."
            </p>
          </WeekAccordion>
          <WeekAccordion week="W2" title="Expression & Release" data-id="element-124">
            <p className="text-gray-700 mb-4 font-medium" data-id="element-125">
              Once your body begins to feel safe, we start supporting healthy
              expression.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-126">
              Anxiety, fear, and emotional pain often get "stuck" in the body.
              You may not even notice it — but tight shoulders, shallow
              breathing, or constant tension are all signs your system hasn't
              had space to release.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-127">
              This week is about creating that space.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-128">
              Not by pushing or forcing — but by giving your body simple,
              structured ways to let go.
            </p>
            <p className="font-semibold text-teal-800 mb-2" data-id="element-129">
              Daily themes include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 font-medium" data-id="element-130">
              <li className="mb-2" data-id="element-131">
                Somatic movements to shake off stored tension
              </li>
              <li className="mb-2" data-id="element-132">
                Breath and sound exercises to create emotional flow
              </li>
              <li className="mb-2" data-id="element-133">
                Guided expression through body, voice, and simple gestures
              </li>
              <li className="mb-2" data-id="element-134">
                Introducing micro-movements for big emotional shifts
              </li>
            </ul>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-135">
              You'll be guided by Taryn Elise Herselman, a yoga therapist and
              psychologist who specializes in restoring calm through Hatha, Yin,
              and Restorative practices. Taryn's sessions feel like being
              wrapped in a warm blanket — supportive, slow, and deeply healing.
            </p>
            <p className="text-gray-700 mb-4 italic font-medium" data-id="element-136">
              By the end of this week, most participants report that their
              nervous system feels noticeably different — softer, more present,
              more regulated.
            </p>
          </WeekAccordion>
          <WeekAccordion week="W3" title="Self-Compassion & Regulation" data-id="element-137">
            <p className="text-gray-700 mb-4 font-medium" data-id="element-138">
              This week is about deepening your relationship with yourself.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-139">
              Once your system starts to release, what rises next is often
              shame, grief, or exhaustion — all the feelings you've been holding
              beneath the surface.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-140">
              That's why we now introduce tools to build real self-compassion
              and strengthen emotional resilience.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-141">
              You'll learn how to show up for yourself with gentleness, without
              spiraling. You'll discover how to stay regulated in the face of
              old triggers — and begin rewriting the internal story that says
              you're too much, too sensitive, or not enough.
            </p>
            <p className="font-semibold text-teal-800 mb-2" data-id="element-142">
              Daily themes include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 font-medium" data-id="element-143">
              <li className="mb-2" data-id="element-144">
                Nervous system-friendly meditation for self-compassion
              </li>
              <li className="mb-2" data-id="element-145">
                Guided vocal toning and breath regulation
              </li>
              <li className="mb-2" data-id="element-146">
                Reframing your internal dialogue through somatic language
              </li>
              <li className="mb-2" data-id="element-147">
                Practices to reparent your body in moments of overwhelm
              </li>
            </ul>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-148">
              You'll be held by Jolena Nicol, a trauma-informed therapist and
              somatic guide who weaves creativity, neuroscience, and embodied
              presence into every practice. Jolena helps you approach even your
              hardest emotions with curiosity, softness, and strength.
            </p>
            <p className="text-gray-700 mb-4 italic font-medium" data-id="element-149">
              Clients often say this is the week they feel a shift in how they
              talk to themselves — a sense of "finally being on their own team."
            </p>
          </WeekAccordion>
          <WeekAccordion week="W4" title="Integration & Resilience" data-id="element-150">
            <p className="text-gray-700 mb-4 font-medium" data-id="element-151">
              By now, your nervous system has begun to find a new rhythm.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-152">
              Your body knows how to ground itself. You've felt what it's like
              to move emotion without panic. You've practiced offering yourself
              compassion instead of criticism.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-153">
              Now it's time to integrate — to bring these tools into your
              everyday life and build a nervous system that doesn't just cope…
              but thrives.
            </p>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-154">
              This final week is about building sustainable, embodied
              resilience.
            </p>
            <p className="font-semibold text-teal-800 mb-2" data-id="element-155">
              Daily themes include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 font-medium" data-id="element-156">
              <li className="mb-2" data-id="element-157">
                Creating your personalized regulation "toolkit"
              </li>
              <li className="mb-2" data-id="element-158">
                Practicing somatic routines for morning and nighttime
              </li>
              <li className="mb-2" data-id="element-159">
                Learning how to recognize dysregulation early — and reset
                quickly
              </li>
              <li className="mb-2" data-id="element-160">
                Honoring your cycles of energy, rest, and emotional needs
              </li>
            </ul>
            <p className="text-gray-700 mb-4 font-medium" data-id="element-161">
              You'll also meet Gabriela Mendelsohn, an art psychotherapist
              deeply inspired by Jung and Viktor Frankl, who introduces
              sensory-based practices to strengthen inner connection. Her work
              shows you how to use creativity — color, image, imagination — to
              access peace and presence in a nonverbal, body-first way.
            </p>
            <p className="text-gray-700 mb-4 italic font-medium" data-id="element-162">
              The final days of the program are designed to help you move
              forward with confidence — equipped not just with knowledge, but
              with lived experience of nervous system regulation.
            </p>
            <p className="text-gray-700 mb-4 italic font-medium" data-id="element-163">
              You'll end not just knowing what safety is — but knowing what it
              feels like.
            </p>
          </WeekAccordion>
        </div>
        <div className="w-full my-8" data-id="element-164">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dkZZRkueQy3MdqDeTmpb38/2058de-c04a-327d-23ba-aa864c213b35_Self-Acupressure_Energy_Balance_for_a_Healthy_Nervous_System_Option_2_1_.jpg" alt="People practicing nervous system regulation techniques" className="w-full max-w-2xl mx-auto h-auto object-cover rounded-lg shadow-md" data-id="element-165" />
        </div>
        <TestimonialsSection data-id="element-166" />
        <main className="w-full max-w-4xl mx-auto px-4 sm:px-6" data-id="element-167">
          <ContentSection title="In my private practice, this kind of support begins at $200 per session." containerClass="bg-gradient-to-r from-teal-100 to-blue-100 shadow-sm" content={<>
                <p className="text-gray-700 mb-4" data-id="element-169">
                  And while I wish I could work one-on-one with everyone who
                  needs it — I simply can't.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-170">
                  That's why I created this program.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-171">
                  The Neurotoned Method gives you the same healing tools I use
                  with my clients… The same structure… The same level of care…
                </p>
                <p className="text-gray-700 mb-4" data-id="element-172">
                  But for a fraction of the cost.
                </p>
              </>} data-id="element-168" />
          <ContentSection title="You've Done Hard Things. Now Let It Be Gentle." containerClass="bg-gradient-to-r from-teal-50 to-blue-50 shadow-sm" content={<>
                <p className="text-gray-700 mb-4" data-id="element-174">
                  You've pushed through enough.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-175">
                  You've survived what your body wasn't meant to carry for so
                  long.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-176">
                  Now it's time for a different kind of healing — one rooted in
                  safety, in science, and in kindness.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-177">
                  The Neurotoned Method is ready when you are.
                </p>
                <p className="text-gray-700 mb-4" data-id="element-178">
                  Let this be the season your body finally remembers how it
                  feels to come home.
                </p>
                <LimitedTimeOffer data-id="element-179" />
                <div className="mt-8 flex justify-center" data-id="element-180">
                  <CTAButton text="Yes — I'm Ready to Reset My Nervous System for Just $29" data-id="element-181" />
                </div>
              </>} data-id="element-173" />
          <div className="w-full my-12" data-id="element-182">
            <PricingCard title="The Neurotoned Method" price="29" features={['30 Guided Daily Reset Sessions', 'Expert Support From 5 Nervous System Practitioners', 'Somatic Tools for Anxiety, Freeze, Panic, and Dissociation', 'Trauma-Informed & Self-Paced', 'Lifetime Access to Everything', 'Risk-Free 30-Day Guarantee', 'One-Time Payment — No Recurring Fees']} data-id="element-183" />
            <div className="mt-6 flex justify-center" data-id="element-184">
              <GuaranteeBadge data-id="element-185" />
            </div>
          </div>
          <ContentSection title="Here's Everything You'll Get When You Join Neurotoned Today:" content={<>
                <div className="space-y-6 mt-6" data-id="element-187">
                  <div className="flex items-start" data-id="element-188">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-189">
                      <span className="text-teal-600" data-id="element-190">1</span>
                    </div>
                    <div data-id="element-191">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-192">
                        30 Guided Daily Reset Sessions
                      </h3>
                      <p className="text-gray-700 font-medium" data-id="element-193">
                        Short, easy-to-follow video practices to calm your
                        nervous system, strengthen your vagus nerve, and get out
                        of fight-or-flight — in just 7–10 minutes a day.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-194">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-195">
                      <span className="text-teal-600" data-id="element-196">2</span>
                    </div>
                    <div data-id="element-197">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-198">
                        Expert Support From 5 Nervous System Practitioners
                      </h3>
                      <p className="text-gray-700 mb-2 font-medium" data-id="element-199">
                        You're not just learning from one person. You're guided
                        by a team of trauma-informed experts including:
                      </p>
                      <ul className="list-disc pl-6 mb-2 text-gray-700 font-medium" data-id="element-200">
                        <li data-id="element-201">
                          Dr. Kim DiRe' (Behavioral Health Doctor + Somatic
                          Therapist)
                        </li>
                        <li data-id="element-202">
                          Taryn Elise Herselman (Yoga Therapist + Psychologist)
                        </li>
                        <li data-id="element-203">
                          Demi-Gea Hart (Holistic Psychologist + Breathwork
                          Guide)
                        </li>
                        <li data-id="element-204">
                          Jolena Nicol (Somatic Therapist + Creative Healing
                          Coach)
                        </li>
                        <li data-id="element-205">
                          Gabriela Mendelsohn (Registered Art Psychotherapist)
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-206">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-207">
                      <span className="text-teal-600" data-id="element-208">3</span>
                    </div>
                    <div data-id="element-209">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-210">
                        Somatic Tools for Anxiety, Freeze, Panic, and
                        Dissociation
                      </h3>
                      <p className="text-gray-700 font-medium" data-id="element-211">
                        Real-world practices you can use anytime your body feels
                        triggered, overwhelmed, or stuck — no talk therapy
                        required.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-212">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-213">
                      <span className="text-teal-600" data-id="element-214">4</span>
                    </div>
                    <div data-id="element-215">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-216">
                        Trauma-Informed & Self-Paced
                      </h3>
                      <p className="text-gray-700 font-medium" data-id="element-217">
                        Move through the program at your own rhythm. Skip,
                        pause, or repeat any session. There's no "falling
                        behind" — just gentle forward momentum.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-218">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-219">
                      <span className="text-teal-600" data-id="element-220">5</span>
                    </div>
                    <div data-id="element-221">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-222">
                        Lifetime Access to Everything
                      </h3>
                      <p className="text-gray-700 font-medium" data-id="element-223">
                        This isn't a subscription. You'll own the full program
                        forever — including all future updates and new
                        practitioner sessions.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-224">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-225">
                      <span className="text-teal-600" data-id="element-226">6</span>
                    </div>
                    <div data-id="element-227">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-228">
                        Risk-Free 30-Day Guarantee
                      </h3>
                      <p className="text-gray-700 font-medium" data-id="element-229">
                        Try it. Feel it. If it doesn't work for you, email us
                        within 30 days for a full refund — no guilt, no
                        questions.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start" data-id="element-230">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center mr-4" data-id="element-231">
                      <span className="text-teal-600" data-id="element-232">7</span>
                    </div>
                    <div data-id="element-233">
                      <h3 className="text-lg font-bold text-gray-800 mb-1" data-id="element-234">
                        One-Time Payment — No Recurring Fees
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="bg-gradient-to-r from-teal-600 to-blue-600 p-8 rounded-lg shadow-lg my-8 text-center" data-id="element-235">
                  <h3 className="text-xl font-bold text-white mb-4" data-id="element-236">
                    Get the Entire 30-Day Neurotoned Program
                  </h3>
                  <p className="text-white mb-6" data-id="element-237">
                    + all expert sessions, bonuses, and lifetime access for just
                    $29
                  </p>
                  <LimitedTimeOffer data-id="element-238" />
                  <div className="flex justify-center" data-id="element-239">
                    <CTAButton text="Unlock the Neurotoned Program — $29 One-Time" data-id="element-240" />
                  </div>
                </div>
              </>} data-id="element-186" />
          <FAQ data-id="element-241" />
          <ContentSection title="You deserve to feel safe in your body again." containerClass="bg-gradient-to-r from-teal-100 to-blue-100 shadow-sm" content={<>
                <p className="text-gray-700 mb-4 text-center font-medium" data-id="element-243">
                  And I'd be honored to help guide you there.
                </p>
                <LimitedTimeOffer data-id="element-244" />
                <div className="mt-8 flex justify-center" data-id="element-245">
                  <CTAButton text="Unlock the Neurotoned Program — $29 One-Time" data-id="element-246" />
                </div>
              </>} data-id="element-242" />
        </main>
      </main>
      <Footer data-id="element-247" />
    </div>;
}